import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';

/**
 * The rounds, read as lanes and batches rather than one lead at a time.
 *
 * ── The question this answers ──────────────────────────────────────────────
 *
 * Kyu, 11 September: "When I click rounds, the rounds view doesn't explain to
 * me what's going on. The batches per round, how each batch performed, how all
 * round 1s perform."
 *
 * Everything the rounds machinery stored was per lead. `lead_round_state` says
 * where one person sits; `lead_loop_state` says whether one person has ever
 * been in a round. Neither can be asked "how did Round 1 do", because the
 * cohort was never a thing you could name. `round_batches` (20270132000100)
 * made it nameable and `round_lane_summary` / `round_batch_summary`
 * (20270138000100) make it readable. This module is the browser's half.
 *
 * ── Two levels, and the vocabulary stays Kyu's ─────────────────────────────
 *
 *   Lane   Round 1, Round 2, Round 3. The loop, run over and over.
 *   Batch  one set of leads started into a lane together.
 *
 * His words: "I'm thinking of the rounds mostly like their own realms or lanes
 * in themselves and in them you can see each batch of those rounds that we
 * did." The types below are named for that, not for the tables.
 *
 * ── Rows missing what they need are DROPPED ────────────────────────────────
 *
 * Every column of a view is nullable to the type generator. The lesson is
 * recorded in leadLoop.ts and it applies harder here: a null `leads` that
 * became 0 would read as "this lane ran for nobody", which is a claim about
 * the business made by a type default.
 *
 * `reply_rate` is the exception and is DELIBERATELY kept nullable all the way
 * to the screen. The view returns null when nothing in the lane has ended yet,
 * and that is not the same as 0%. A fresh batch showing "0% replied" on its
 * first morning is the single most misreadable number this page could print.
 */

export interface RoundLane {
  number: number;
  leads: number;
  batches: number;
  unbatchedLeads: number;
  runningRounds: number;
  endedRounds: number;
  replied: number;
  noAnswer: number;
  countMet: number;
  touches: number;
  /** Null until something in the lane has ended. Never coerced to zero. */
  replyRate: number | null;
  touchesPerLead: number | null;
  usualTarget: number | null;
  firstStarted: string | null;
  lastStarted: string | null;
}

export interface RoundBatch {
  batchId: string;
  number: number;
  name: string;
  startsOn: string;
  touchTarget: number;
  windowLength: number;
  windowUnit: string;
  leads: number;
  runningRounds: number;
  endedRounds: number;
  replied: number;
  noAnswer: number;
  countMet: number;
  touches: number;
  /** Null until something in the batch has ended. See the header. */
  replyRate: number | null;
  touchesPerLead: number | null;
  isRunning: boolean;
  lastEndedOn: string | null;
}

// ── Reading them ───────────────────────────────────────────────────────────

export const laneKeys = {
  lanes: () => [...trackerKeys.all, 'round-lanes'] as const,
  batches: () => [...trackerKeys.all, 'round-batches'] as const,
};

/**
 * A count that must be a number to mean anything.
 *
 * Returns null rather than 0 for a missing value, so the caller drops the row
 * instead of publishing a zero nobody measured.
 */
function count(value: number | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export async function fetchRoundLanes(client: SupabaseClient): Promise<RoundLane[]> {
  const { data, error } = await client.from('round_lane_summary').select('*');
  if (error) {
    throw new Error(error.message);
  }
  const lanes: RoundLane[] = [];
  for (const row of data ?? []) {
    const number = count(row.number);
    const leads = count(row.leads);
    const ended = count(row.ended_rounds);
    const running = count(row.running_rounds);
    if (number === null || leads === null || ended === null || running === null) {
      continue;
    }
    lanes.push({
      number,
      leads,
      batches: count(row.batches) ?? 0,
      unbatchedLeads: count(row.unbatched_leads) ?? 0,
      runningRounds: running,
      endedRounds: ended,
      replied: count(row.replied) ?? 0,
      noAnswer: count(row.no_answer) ?? 0,
      countMet: count(row.count_met) ?? 0,
      touches: count(row.touches) ?? 0,
      replyRate: count(row.reply_rate),
      touchesPerLead: count(row.touches_per_lead),
      usualTarget: count(row.usual_target),
      firstStarted: row.first_started,
      lastStarted: row.last_started,
    });
  }
  return lanes.sort((a, b) => a.number - b.number);
}

export async function fetchRoundBatches(client: SupabaseClient): Promise<RoundBatch[]> {
  const { data, error } = await client.from('round_batch_summary').select('*');
  if (error) {
    throw new Error(error.message);
  }
  const batches: RoundBatch[] = [];
  for (const row of data ?? []) {
    const number = count(row.number);
    const leads = count(row.leads);
    if (
      row.batch_id === null ||
      row.name === null ||
      row.starts_on === null ||
      number === null ||
      leads === null
    ) {
      continue;
    }
    batches.push({
      batchId: row.batch_id,
      number,
      name: row.name,
      startsOn: row.starts_on,
      touchTarget: count(row.touch_target) ?? 0,
      windowLength: count(row.window_length) ?? 0,
      windowUnit: row.window_unit ?? 'business_days',
      leads,
      runningRounds: count(row.running_rounds) ?? 0,
      endedRounds: count(row.ended_rounds) ?? 0,
      replied: count(row.replied) ?? 0,
      noAnswer: count(row.no_answer) ?? 0,
      countMet: count(row.count_met) ?? 0,
      touches: count(row.touches) ?? 0,
      replyRate: count(row.reply_rate),
      touchesPerLead: count(row.touches_per_lead),
      isRunning: row.is_running === true,
      lastEndedOn: row.last_ended_on,
    });
  }
  // Newest batch first inside each lane: the one you started this week is the
  // one you came to look at.
  return batches.sort((a, b) => a.number - b.number || b.startsOn.localeCompare(a.startsOn));
}

export function useRoundLanes() {
  const { client } = useAuth();
  return useQuery({
    queryKey: laneKeys.lanes(),
    queryFn: () => fetchRoundLanes(client),
    retry: false,
  });
}

export function useRoundBatches() {
  const { client } = useAuth();
  return useQuery({
    queryKey: laneKeys.batches(),
    queryFn: () => fetchRoundBatches(client),
    retry: false,
  });
}

// ── What the page says about them ──────────────────────────────────────────
//
// Pure, and separated from the component on the house rule: the decision gets
// a test, the component only gets checked for wiring.

/**
 * The reply rate, or an honest refusal to state one.
 *
 * Kyu's standing rule, applied to a number: if you would not say it out loud,
 * it does not go on the page. "0%" about a batch where nobody has finished yet
 * is a sentence nobody would say.
 */
export function replyRateLabel(rate: number | null, endedRounds: number): string {
  if (rate === null || endedRounds === 0) {
    return 'Not yet';
  }
  return `${String(rate)}%`;
}

/** The denominator, spelled out, because the number is meaningless without it. */
export function replyRateBasis(replied: number, endedRounds: number): string {
  if (endedRounds === 0) {
    return 'nothing has ended yet';
  }
  return `${String(replied)} of ${String(endedRounds)} ended`;
}

/** "5 follow-ups over 10 business days" — the shape the batch was started with. */
export function batchShapeLabel(batch: RoundBatch): string {
  const touches = `${String(batch.touchTarget)} follow-up${batch.touchTarget === 1 ? '' : 's'}`;
  const unit = batch.windowUnit === 'weeks' ? 'week' : 'business day';
  const window = `${String(batch.windowLength)} ${unit}${batch.windowLength === 1 ? '' : 's'}`;
  return `${touches} over ${window}`;
}

export interface OutcomeSegment {
  key: 'replied' | 'count_met' | 'no_answer' | 'running';
  label: string;
  count: number;
}

/**
 * The four ways a round in this batch can currently stand.
 *
 * Returned as counts, never as percentages. The bar is drawn with flex-grow,
 * so the browser divides the width and no rounding happens at all — the
 * version that computed percentages could show four segments summing to 101%.
 *
 * Zero-count segments are dropped rather than rendered at zero width, because
 * a 0px segment still draws its border and reads as a hairline of a colour
 * that means something.
 */
export function outcomeSegments(batch: {
  replied: number;
  countMet: number;
  noAnswer: number;
  runningRounds: number;
}): OutcomeSegment[] {
  return (
    [
      { key: 'replied' as const, label: 'Replied', count: batch.replied },
      { key: 'count_met' as const, label: 'Ran the full course', count: batch.countMet },
      { key: 'no_answer' as const, label: 'No answer', count: batch.noAnswer },
      { key: 'running' as const, label: 'Still running', count: batch.runningRounds },
    ] satisfies OutcomeSegment[]
  ).filter((segment) => segment.count > 0);
}

/**
 * What the lane header says under the round number.
 *
 * Running is named even when it is zero-ish, because "12 leads" with no split
 * invites the reader to treat all twelve as finished.
 */
export function laneSummaryLine(lane: RoundLane): string {
  const parts = [`${String(lane.leads)} lead${lane.leads === 1 ? '' : 's'}`];
  if (lane.endedRounds > 0) {
    parts.push(`${String(lane.endedRounds)} ended`);
  }
  if (lane.runningRounds > 0) {
    parts.push(`${String(lane.runningRounds)} running`);
  }
  return parts.join(' · ');
}

/**
 * The gap between a lane's leads and the sum of its batches.
 *
 * Not an error. The ten rounds already on record were started one lead at a
 * time before batches existed, so they belong to a lane and to no batch. Said
 * on the page rather than left as an unexplained subtraction, which is how a
 * reader decides the numbers are broken.
 */
export function unbatchedNote(lane: RoundLane): string | null {
  if (lane.unbatchedLeads === 0) {
    return null;
  }
  const leads = `${String(lane.unbatchedLeads)} lead${lane.unbatchedLeads === 1 ? '' : 's'}`;
  return `${leads} in this lane were started one at a time, before batches existed. They count in the totals above and appear in no batch below.`;
}
